import { isoDateToUrlDate } from 'common/UrlDate';
import { MealPlanDisplayFoodItemFragment, MealPlanShefFragment } from 'src/gqlReactTypings.generated.d';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { useCartStore } from '../cart-store';
import { createSelectors } from '../create-selectors';

interface State {
  foodItems: Record<number, MealPlanDisplayFoodItemFragment>;
  shefs: Record<string, MealPlanShefFragment>;
  lastPopulatedAt: number | null;
  populatedForDeliveryDate: string | null;
}

interface Actions {
  setFoodItem: (foodItem: MealPlanDisplayFoodItemFragment) => void;
  setShef: (shef: MealPlanShefFragment) => void;
  bulkSetFoodItems: (foodItems: MealPlanDisplayFoodItemFragment[]) => void;
  bulkSetShefs: (shefs: MealPlanShefFragment[]) => void;
  markPopulated: (deliveryDate: string) => void;
  refreshFoodItems: (foodItems: MealPlanDisplayFoodItemFragment[]) => void;
  refreshShefs: (shefs: MealPlanShefFragment[]) => void;
  clear: () => void;
}

function getNumAvailableForDate(
  foodItem: MealPlanDisplayFoodItemFragment,
  deliveryDate: string
): number | null | undefined {
  return foodItem.availability?.find((a) => isoDateToUrlDate(a.availabilityDate) === deliveryDate)?.numAvailable;
}

function orderedArraysEqual(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, i) => value === b[i]);
}

// Flagged ingredients change when the user edits their dietary/avoided-ingredient
// inputs and the menu query refetches — so a re-population must overwrite them.
function flaggedIngredientsChanged(
  existing: MealPlanDisplayFoodItemFragment,
  next: MealPlanDisplayFoodItemFragment
): boolean {
  return (
    !orderedArraysEqual(existing.ingredientsFlagged, next.ingredientsFlagged) ||
    !orderedArraysEqual(existing.customIngredientsFlagged, next.customIngredientsFlagged)
  );
}

export const INITIAL_STATE: State = {
  foodItems: {},
  shefs: {},
  lastPopulatedAt: null,
  populatedForDeliveryDate: null,
};

export const useMealPlanCatalogStore = create(
  immer<State & Actions>((set, get) => ({
    ...INITIAL_STATE,
    setFoodItem: (foodItem: MealPlanDisplayFoodItemFragment) => {
      if (get().foodItems[foodItem.id]) return;
      set((state) => {
        state.foodItems[foodItem.id] = foodItem;
      });
    },
    setShef: (shef: MealPlanShefFragment) => {
      if (get().shefs[shef.id]) return;
      set((state) => {
        state.shefs[shef.id] = shef;
      });
    },
    bulkSetFoodItems: (foodItems: MealPlanDisplayFoodItemFragment[]) => {
      const deliveryDate = useCartStore.getState().deliveryDate ?? '';
      set((state) => {
        for (const foodItem of foodItems) {
          const existing = state.foodItems[foodItem.id];
          if (
            !existing ||
            getNumAvailableForDate(existing, deliveryDate) !== getNumAvailableForDate(foodItem, deliveryDate) ||
            flaggedIngredientsChanged(existing, foodItem)
          ) {
            state.foodItems[foodItem.id] = foodItem;
          }
        }
      });
    },
    bulkSetShefs: (shefs: MealPlanShefFragment[]) => {
      set((state) => {
        for (const shef of shefs) {
          const existing = state.shefs[shef.id];
          if (!existing || existing.availabilityForDate !== shef.availabilityForDate) {
            state.shefs[shef.id] = shef;
          }
        }
      });
    },
    markPopulated: (deliveryDate: string) => {
      set((state) => {
        state.lastPopulatedAt = Date.now();
        state.populatedForDeliveryDate = deliveryDate;
      });
    },
    refreshFoodItems: (foodItems: MealPlanDisplayFoodItemFragment[]) => {
      set((state) => {
        for (const foodItem of foodItems) {
          state.foodItems[foodItem.id] = foodItem;
        }
      });
    },
    refreshShefs: (shefs: MealPlanShefFragment[]) => {
      set((state) => {
        for (const shef of shefs) {
          state.shefs[shef.id] = shef;
        }
      });
    },
    clear: () => {
      set(INITIAL_STATE);
    },
  }))
);

export const useMealPlanCatalogStoreSelectors = createSelectors(useMealPlanCatalogStore);
